import i18n, { type TOptions } from 'i18next'

// ── English ──────────────────────────────────────────────────────────────────
import enAdmin from './locales/en/admin.json'
import enAnalysisPages from './locales/en/analysisPages.json'
import enAnalyzer from './locales/en/analyzer.json'
import enAppMenu from './locales/en/appMenu.json'
import enAuth from './locales/en/auth.json'
import enCommon from './locales/en/common.json'
import enComponents from './locales/en/components.json'
import enErrors from './locales/en/errors.json'
import enExplore from './locales/en/explore.json'
import enExtensionOptions from './locales/en/extensionOptions.json'
import enIndex from './locales/en/index.json'
import enLinaPanel from './locales/en/linaPanel.json'
import enMessageInput from './locales/en/messageInput.json'
import enNav from './locales/en/nav.json'
import enResponse from './locales/en/response.json'
import enSettings from './locales/en/settings.json'
import enSettingsComponents from './locales/en/settingsComponents.json'
import enTokens from './locales/en/tokens.json'
import enUserSettings from './locales/en/userSettings.json'
import enWebExtension from './locales/en/webExtension.json'

// ── French (Canada) ──────────────────────────────────────────────────────────
import frCAAdmin from './locales/fr-CA/admin.json'
import frCAAnalysisPages from './locales/fr-CA/analysisPages.json'
import frCAAnalyzer from './locales/fr-CA/analyzer.json'
import frCAAppMenu from './locales/fr-CA/appMenu.json'
import frCAAuth from './locales/fr-CA/auth.json'
import frCACommon from './locales/fr-CA/common.json'
import frCAComponents from './locales/fr-CA/components.json'
import frCAErrors from './locales/fr-CA/errors.json'
import frCAExplore from './locales/fr-CA/explore.json'
import frCAExtensionOptions from './locales/fr-CA/extensionOptions.json'
import frCAIndex from './locales/fr-CA/index.json'
import frCALinaPanel from './locales/fr-CA/linaPanel.json'
import frCAMessageInput from './locales/fr-CA/messageInput.json'
import frCANav from './locales/fr-CA/nav.json'
import frCAResponse from './locales/fr-CA/response.json'
import frCASettings from './locales/fr-CA/settings.json'
import frCASettingsComponents from './locales/fr-CA/settingsComponents.json'
import frCATokens from './locales/fr-CA/tokens.json'
import frCAUserSettings from './locales/fr-CA/userSettings.json'
import frCAWebExtension from './locales/fr-CA/webExtension.json'

export const LANGUAGE_STORAGE_KEY = 'language'
export const SUPPORTED_LANGUAGES = ['en', 'fr-CA'] as const

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

const resources = {
  en: {
    translation: {
      admin: enAdmin,
      analysisPages: enAnalysisPages,
      analyzer: enAnalyzer,
      appMenu: enAppMenu,
      auth: enAuth,
      common: enCommon,
      components: enComponents,
      errors: enErrors,
      explore: enExplore,
      extensionOptions: enExtensionOptions,
      index: enIndex,
      linaPanel: enLinaPanel,
      messageInput: enMessageInput,
      nav: enNav,
      response: enResponse,
      settings: enSettings,
      settingsComponents: enSettingsComponents,
      tokens: enTokens,
      userSettings: enUserSettings,
      webExtension: enWebExtension,
    },
  },
  'fr-CA': {
    translation: {
      admin: frCAAdmin,
      analysisPages: frCAAnalysisPages,
      analyzer: frCAAnalyzer,
      appMenu: frCAAppMenu,
      auth: frCAAuth,
      common: frCACommon,
      components: frCAComponents,
      errors: frCAErrors,
      explore: frCAExplore,
      extensionOptions: frCAExtensionOptions,
      index: frCAIndex,
      linaPanel: frCALinaPanel,
      messageInput: frCAMessageInput,
      nav: frCANav,
      response: frCAResponse,
      settings: frCASettings,
      settingsComponents: frCASettingsComponents,
      tokens: frCATokens,
      userSettings: frCAUserSettings,
      webExtension: frCAWebExtension,
    },
  },
} as const

const globalWithChrome = globalThis as typeof globalThis & {
  chrome?: {
    i18n?: {
      getUILocale: () => string
    }
    storage?: {
      sync?: {
        get: (keys: string[] | string) => Promise<Record<string, unknown>>
        set: (items: Record<string, unknown>) => Promise<void>
      }
    }
  }
}

let initPromise: Promise<void> | null = null
let missingListenersBound = false

function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return typeof value === 'string' && SUPPORTED_LANGUAGES.includes(value as SupportedLanguage)
}

/** Normalizes locale tags such as fr_CA → fr-ca for comparison */
function normalizeLocaleTag(tag: string): string {
  return tag.trim().toLowerCase().replace(/_/g, '-')
}

/**
 * Maps a browser / Chrome UI locale tag to English or Français (Canada).
 * Anything not treated as English or Canadian French returns null (caller falls back to "en").
 */
export function mapBrowserLocaleTagToSupportedLanguage(
  localeTag: string | null | undefined,
): SupportedLanguage | null {
  const raw = String(localeTag ?? '').trim()
  if (!raw) return null
  const n = normalizeLocaleTag(raw)

  if (n === 'en' || n.startsWith('en-')) {
    return 'en'
  }
  if (n === 'fr-ca' || n.startsWith('fr-ca-')) {
    return 'fr-CA'
  }
  if (n === 'fr') {
    return 'fr-CA'
  }

  return null
}

function getChromeOrNavigatorLocaleTags(): string[] {
  const ordered: string[] = []
  try {
    const chromeLocale = globalWithChrome.chrome?.i18n?.getUILocale?.()
    const ui = chromeLocale?.trim()
    if (ui) ordered.push(ui)
  } catch {
    // Missing i18n API or restricted context.
  }
  try {
    if (typeof navigator !== 'undefined') {
      for (const l of navigator.languages ?? []) {
        if (l && typeof l === 'string' && l.trim()) ordered.push(l.trim())
      }
      if (navigator.language?.trim()) ordered.push(navigator.language.trim())
    }
  } catch {
    // Ignore Navigator access failures.
  }

  const seen = new Set<string>()
  const unique: string[] = []
  for (const tag of ordered) {
    const k = normalizeLocaleTag(tag)
    if (seen.has(k)) continue
    seen.add(k)
    unique.push(tag)
  }
  return unique
}

function resolvePreferredLanguageFromBrowserUi(): SupportedLanguage {
  for (const tag of getChromeOrNavigatorLocaleTags()) {
    const mapped = mapBrowserLocaleTagToSupportedLanguage(tag)
    if (mapped) return mapped
  }
  return 'en'
}

function getSessionLanguage(): SupportedLanguage | null {
  try {
    if (typeof window === 'undefined') return null
    const value = window.sessionStorage.getItem(LANGUAGE_STORAGE_KEY)
    return isSupportedLanguage(value) ? value : null
  } catch {
    return null
  }
}

function setSessionLanguage(language: SupportedLanguage): void {
  try {
    if (typeof window === 'undefined') return
    window.sessionStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  } catch {
    // Ignore storage failures (private mode / blocked storage).
  }
}

/** Read persisted UI language from `chrome.storage.sync` (extension background / MV3 worker). Does not initialize i18n. */
export async function getChromeSyncLanguage(): Promise<SupportedLanguage | null> {
  const sync = globalWithChrome.chrome?.storage?.sync
  if (!sync) return null
  try {
    const result = await sync.get([LANGUAGE_STORAGE_KEY])
    const value = result[LANGUAGE_STORAGE_KEY]
    return isSupportedLanguage(value) ? value : null
  } catch {
    return null
  }
}

async function setChromeSyncLanguage(language: SupportedLanguage): Promise<void> {
  const sync = globalWithChrome.chrome?.storage?.sync
  if (!sync) return
  try {
    await sync.set({ [LANGUAGE_STORAGE_KEY]: language })
  } catch {
    // Ignore chrome storage failures; session storage is still persisted.
  }
}

function bindMissingKeyWarnings(): void {
  if (missingListenersBound) return
  missingListenersBound = true

  i18n.on('missingKey', (_, __, key) => {
    console.warn(`[i18n] Missing key: ${key}`)
  })
}

export async function resolveInitialLanguage(options?: {
  preferChromeStorage?: boolean
}): Promise<SupportedLanguage> {
  if (options?.preferChromeStorage) {
    const chromeLanguage = await getChromeSyncLanguage()
    if (chromeLanguage) return chromeLanguage
  }

  const sessionLanguage = getSessionLanguage()
  if (sessionLanguage) return sessionLanguage

  return resolvePreferredLanguageFromBrowserUi()
}

export async function initI18n(options?: { preferChromeStorage?: boolean }): Promise<typeof i18n> {
  if (i18n.isInitialized) {
    bindMissingKeyWarnings()
    return i18n
  }

  if (!initPromise) {
    initPromise = (async () => {
      const initialLanguage = await resolveInitialLanguage(options)
      await i18n.init({
        resources,
        fallbackLng: 'en',
        lng: initialLanguage,
        interpolation: { escapeValue: false },
        returnNull: false,
      })
      bindMissingKeyWarnings()
      setSessionLanguage(initialLanguage)
      if (options?.preferChromeStorage) {
        await setChromeSyncLanguage(initialLanguage)
      }
    })()
  }

  await initPromise
  return i18n
}

export async function changeLanguage(
  language: SupportedLanguage,
  options?: { persistChromeStorage?: boolean },
): Promise<void> {
  await initI18n({ preferChromeStorage: options?.persistChromeStorage })
  await i18n.changeLanguage(language)
  setSessionLanguage(language)
  if (options?.persistChromeStorage) {
    await setChromeSyncLanguage(language)
  }
}

export function t(key: string, options?: TOptions): string {
  const currentLanguage = isSupportedLanguage(i18n.language) ? i18n.language : 'en'
  if (i18n.exists(key, { lng: currentLanguage })) {
    return String(i18n.t(key, options as never))
  }

  if (currentLanguage !== 'en' && i18n.exists(key, { lng: 'en' })) {
    console.warn(`[i18n] Missing key in ${currentLanguage}, falling back to en: ${key}`)
    return String(i18n.t(key, { ...options, lng: 'en' } as never))
  }

  console.warn(`[i18n] Missing key in all locales: ${key}`)
  return key
}

export { resources }
export default i18n
